import { defaults } from 'lodash-es'

import type { PositionChangePayload } from './WeveDraggable.types'
import { isBefore } from './WeveDraggable.util'

interface Options {
  sort?: boolean
  onDragStart?: (e: DragEvent) => void
  onDragEnd?: () => void
  onUpdate?: (payload: PositionChangePayload) => void
}

const DEFAULT_OPTIONS: Options = { sort: true }

export default class WeveDraggable {
  private current: HTMLElement | null = null
  private readonly options: Options
  private activated = false

  constructor(private readonly container: HTMLElement, options: Options = {}) {
    this.options = defaults({}, options, DEFAULT_OPTIONS)
    const { sort } = this.options
    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onDragStart = this.withActivated(this.onDragStart.bind(this))
    this.onDragEnter = this.withActivated(this.onDragEnter.bind(this))
    this.onDragOver = this.withActivated(this.onDragOver.bind(this))
    this.onDragEnd = this.withActivated(this.onDragEnd.bind(this))
    container.addEventListener('mousedown', this.onMouseDown)
    container.addEventListener('mouseup', this.onMouseUp)
    container.addEventListener('dragstart', this.onDragStart)
    if (sort) {
      container.addEventListener('dragenter', this.onDragEnter)
      container.addEventListener('dragover', this.onDragOver)
    }
    container.addEventListener('dragend', this.onDragEnd)
  }

  private onMouseDown(e: MouseEvent) {
    this.onMouseEvent(e, 'mousedown')
  }

  private onMouseUp(e: MouseEvent) {
    this.onMouseEvent(e, 'mouseup')
  }

  private onMouseEvent(e: MouseEvent, eventName: 'mousedown' | 'mouseup') {
    const target = e.target as HTMLElement
    const handle = target.closest('.handle')
    if (handle !== null) {
      const closestDraggableElement = this.findClosestDraggableElement(target)
      if (closestDraggableElement === null) {
        return
      }
      if (eventName === 'mousedown') {
        this.activated = true
        closestDraggableElement.setAttribute('draggable', 'true')
      } else {
        closestDraggableElement.removeAttribute('draggable')
        this.activated = false
      }
    }
  }

  private onDragStart(e: DragEvent) {
    const { dataTransfer } = e
    const target = e.target as HTMLElement
    if (dataTransfer !== null) {
      dataTransfer.effectAllowed = 'move'
      dataTransfer.dropEffect = 'move'
      if (target.dataset.id !== undefined) {
        dataTransfer.setData('id', target.dataset.id)
      }
    }
    target.classList.add('--dragging')
    this.current = target
    if (this.options.onDragStart !== undefined) {
      this.options.onDragStart(e)
    }
  }

  private onDragEnter(e: DragEvent) {
    const { current } = this
    if (current === null) {
      return
    }
    e.preventDefault()
    const target = e.target as HTMLElement
    const item = this.findClosestDraggableElement(target)
    if (item === null) {
      return
    }
    const parentNode = item.parentNode as HTMLElement
    if (isBefore(current, item)) {
      parentNode.insertBefore(current, item)
    } else {
      parentNode.insertBefore(current, item.nextSibling)
    }
  }

  private onDragOver(e: DragEvent) {
    e.preventDefault()
  }

  private onDragEnd() {
    const { current } = this
    if (current === null) {
      return
    }
    if (this.options.sort) {
      const oldIndex = WeveDraggable.getElementIndex(current)
      let newIndex = oldIndex
      if (current.nextElementSibling !== null) {
        const index = WeveDraggable.getElementIndex(
          current.nextElementSibling as HTMLElement
        )
        newIndex = index > oldIndex ? index - 1 : index
      } else if (
        current.parentNode !== null &&
        current === current.parentNode.lastElementChild
      ) {
        newIndex = current.parentNode.children.length - 1
      } else {
        newIndex = 0
      }
      if (oldIndex !== newIndex && this.options.onUpdate !== undefined) {
        this.options.onUpdate({ oldIndex, newIndex })
      }
    }
    current.removeAttribute('draggable')
    current.classList.remove('--dragging')
    this.current = null
    if (this.options.onDragEnd !== undefined) {
      this.options.onDragEnd()
    }
  }

  private findClosestDraggableElement(el: Element | null): Element | null {
    while (el !== null && el.parentNode !== null) {
      el = el.parentElement

      if (el === null) {
        return null
      }

      if (el.parentElement === this.container) {
        return el
      }
    }

    return null
  }

  private withActivated<T extends (...args: any[]) => void>(fn: T) {
    const self = this
    return function (this: WeveDraggable, ...args: any[]) {
      if (self.activated === false) {
        return
      }
      return fn.call(this, ...args)
    }
  }

  destroy() {
    this.container.removeEventListener('mousedown', this.onMouseDown)
    this.container.removeEventListener('mouseup', this.onMouseUp)
    this.container.removeEventListener('dragstart', this.onDragStart)
    this.container.removeEventListener('dragenter', this.onDragEnter)
    this.container.removeEventListener('dragover', this.onDragOver)
    this.container.removeEventListener('dragend', this.onDragEnd)
  }

  static extractIdFromDragEvent(e: DragEvent): undefined | number {
    const { dataTransfer } = e
    if (dataTransfer === null) {
      return undefined
    }
    const rawData = dataTransfer.getData('id')
    if (rawData === '') {
      return undefined
    }
    const id = parseInt(rawData)
    if (Number.isNaN(id)) {
      return undefined
    }
    return id
  }

  static getElementIndex(el: HTMLElement): number {
    if (el.dataset.index !== undefined) {
      const value = parseInt(el.dataset.index)
      if (Number.isNaN(value) === false) {
        return value
      }
    }
    if (el.parentElement !== null) {
      Array.from(el.parentElement.children).indexOf(el)
    }
    return -1
  }
}
